import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Invoice, InvoicePreview, RateLimit } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshButton } from '@/components/RefreshButton';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Loader2, FileText, Clock, Receipt, AlertCircle, Info } from 'lucide-react';

// Helper to get current period in YYYY-MM format
function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Helper to format period for display
function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function BillingPage() {
  const [activeTab, setActiveTab] = useState('preview');
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentPeriod = getCurrentPeriod();

  useEffect(() => {
    loadPreview();
    loadInvoices();
  }, []);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    setError(null);
    try {
      const response = await api.getInvoicePreview(currentPeriod);
      setPreview(response.preview);
      if (response.rate_limit) {
        setRateLimit(response.rate_limit);
      }
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load billing preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const loadInvoices = async () => {
    setIsLoadingInvoices(true);
    try {
      const response = await api.getInvoices();
      setInvoices(response.invoices);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const response = await api.refreshInvoicePreview(currentPeriod);
      setPreview(response.preview);
      if (response.rate_limit) {
        setRateLimit(response.rate_limit);
      }
    } catch (err: unknown) {
      console.error('Failed to refresh preview:', err);
      const apiError = err as { error?: string };
      setError(apiError.error || 'Failed to refresh preview');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
          <p className="text-gray-600 mt-1">View your charges and invoices</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Upcoming
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle>Upcoming charges (estimate)</CardTitle>
                    <CardDescription className="mt-1">
                      {formatPeriod(currentPeriod)}
                    </CardDescription>
                  </div>
                  {rateLimit && (
                    <RefreshButton
                      onClick={handleRefresh}
                      loading={isRefreshing}
                      rateLimit={rateLimit}
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                {isLoadingPreview ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
                  </div>
                ) : !preview ? (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No preview available for this period.</p>
                    <Button onClick={handleRefresh} disabled={isRefreshing}>
                      {isRefreshing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        'Generate Preview'
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* As-of timestamp */}
                    <div className="mb-4 text-sm text-gray-500 flex items-center gap-1">
                      <Info className="h-4 w-4" />
                      As of {new Date(preview.as_of).toLocaleString()}. Data may be up to 60 minutes old.
                    </div>

                    {/* Totals Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-700 font-medium mb-3">Billed so far (Actual)</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-green-600">Retail</span>
                            <span className="text-lg font-semibold text-green-700">{formatCurrency(preview.total_retail_actual)}</span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-green-600">Your cost ({Math.round(preview.wholesale_percent_used * 100)}%)</span>
                            <span className="text-xl font-bold text-green-800">{formatCurrency(preview.total_wholesale_actual)}</span>
                          </div>
                          <div className="flex justify-between items-baseline pt-1 border-t border-green-200">
                            <span className="text-sm text-green-600">You earned</span>
                            <span className="text-sm font-medium text-green-700">{formatCurrency(preview.total_retail_actual - preview.total_wholesale_actual)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-700 font-medium mb-3">Expected later (Forecast)</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-yellow-600">Retail</span>
                            <span className="text-lg font-semibold text-yellow-700">{formatCurrency(preview.total_retail_forecast)}</span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-yellow-600">Your cost ({Math.round(preview.wholesale_percent_used * 100)}%)</span>
                            <span className="text-xl font-bold text-yellow-800">{formatCurrency(preview.total_wholesale_forecast)}</span>
                          </div>
                          <div className="flex justify-between items-baseline pt-1 border-t border-yellow-200">
                            <span className="text-sm text-yellow-600">You will earn</span>
                            <span className="text-sm font-medium text-yellow-700">{formatCurrency(preview.total_retail_forecast - preview.total_wholesale_forecast)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-700 font-medium mb-3">Estimated Monthly Total</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-blue-600">Retail</span>
                            <span className="text-lg font-semibold text-blue-700">{formatCurrency(preview.total_retail_actual + preview.total_retail_forecast)}</span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-blue-600">Your cost ({Math.round(preview.wholesale_percent_used * 100)}%)</span>
                            <span className="text-xl font-bold text-blue-800">{formatCurrency(preview.total_wholesale_actual + preview.total_wholesale_forecast)}</span>
                          </div>
                          <div className="flex justify-between items-baseline pt-1 border-t border-blue-200">
                            <span className="text-sm text-blue-600">Total earnings</span>
                            <span className="text-sm font-medium text-blue-700">{formatCurrency((preview.total_retail_actual + preview.total_retail_forecast) - (preview.total_wholesale_actual + preview.total_wholesale_forecast))}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Line Items */}
                    {preview.lines.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No charges for this period yet.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-gray-500">
                              <th className="pb-3 font-medium">Type</th>
                              <th className="pb-3 font-medium">Account</th>
                              <th className="pb-3 font-medium">Product</th>
                              <th className="pb-3 font-medium">Close Date</th>
                              <th className="pb-3 font-medium text-right">Retail</th>
                              <th className="pb-3 font-medium text-right">Wholesale</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {preview.lines.map((line) => (
                              <tr key={line.id} className="hover:bg-gray-50">
                                <td className="py-3">
                                  {line.line_kind === 'actual' ? (
                                    <span
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                      title="This charge is from a billed opportunity in Salesforce."
                                    >
                                      Actual (billed)
                                    </span>
                                  ) : (
                                    <span
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                                      title="This is an estimate from a staging opportunity and may change before billing."
                                    >
                                      Forecast (staging)
                                    </span>
                                  )}
                                </td>
                                <td className="py-3">
                                  <div>
                                    <p className="font-medium text-gray-900">{line.customer_account_name}</p>
                                  </div>
                                </td>
                                <td className="py-3 text-gray-600">{line.product_name}</td>
                                <td className="py-3 text-gray-600">{formatDate(line.close_date)}</td>
                                <td className="py-3 text-right text-gray-600">
                                  {formatCurrency(line.retail_amount)}
                                </td>
                                <td className="py-3 text-right font-medium text-gray-900">
                                  {formatCurrency(line.wholesale_amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Invoices</CardTitle>
                <CardDescription>
                  Invoices are final once issued. If something is billed late or corrected,
                  it will appear on a future invoice as an adjustment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingInvoices ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No invoices yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-3 font-medium">Invoice #</th>
                          <th className="pb-3 font-medium">Billing Period</th>
                          <th className="pb-3 font-medium">Issued Date</th>
                          <th className="pb-3 font-medium text-right">Total (Wholesale)</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="py-3 font-medium text-gray-900">
                              {invoice.invoice_number}
                            </td>
                            <td className="py-3 text-gray-600">
                              {formatPeriod(`${invoice.period_start.substring(0, 7)}`)}
                            </td>
                            <td className="py-3 text-gray-600">
                              {formatDate(invoice.issued_at)}
                            </td>
                            <td className="py-3 text-right font-medium text-gray-900">
                              {formatCurrency(invoice.total_wholesale)}
                            </td>
                            <td className="py-3">
                              <InvoiceStatusBadge status={invoice.status} />
                            </td>
                            <td className="py-3">
                              <Link to={`/billing/invoices/${invoice.id}`}>
                                <Button variant="ghost" size="sm">View</Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    issued: 'bg-blue-100 text-blue-800',
    sent: 'bg-purple-100 text-purple-800',
    paid: 'bg-green-100 text-green-800',
    void: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<string, string> = {
    issued: 'Issued',
    sent: 'Sent',
    paid: 'Paid',
    void: 'Void',
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.issued}`}>
      {labels[status] || status}
    </span>
  );
}
